"use client";

import React, { useEffect, useRef, useState } from "react";
import { Input } from "./Input";
import { RiMapPinLine } from "react-icons/ri";
import { Label } from "../shadcn/Label";
import { If } from "../Conditional";
import { cn } from "~/utils/fontsConfig";

type GoogleMapsAutoCompleteProps = {
    value?: GooglePlace | null;
    setValue: (value: GooglePlace) => void;
    isEditing?: boolean;
    children?: React.ReactNode;
    className?: string;
    placeholder?: string;
    label?: boolean;
    required?: boolean;
};

const GoogleMapsAutoComplete = ({
    className,
    value,
    setValue,
    children,
    placeholder = "Search location...",
    label = true,
    required,
}: GoogleMapsAutoCompleteProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [inputValue, setInputValue] = useState<string>("");
    const [isApiLoaded, setIsApiLoaded] = useState(false);
    const [options, setOptions] = useState<
        google.maps.places.AutocompletePrediction[]
    >([]);
    const [showOptions, setShowOptions] = useState(false);

    useEffect(() => {
        // Check if the Google Maps API is available
        const checkGoogleMapsApi = () => {
            if (window.google?.maps?.places) {
                setIsApiLoaded(true);
                return;
            }
            setTimeout(checkGoogleMapsApi, 100);
        };
        checkGoogleMapsApi();
    }, []);

    useEffect(() => {
        const addressParts = [];
        if (value?.name) {
            addressParts.push(value.name);
        }
        if (value?.formatted_address) {
            addressParts.push(value.formatted_address);
        }
        setInputValue(addressParts.join(", "));
    }, [value]);

    // Fetch predictions on input change
    useEffect(() => {
        if (!isApiLoaded || !inputValue) {
            setOptions([]);
            return;
        }
        const service = new google.maps.places.AutocompleteService();
        service.getPlacePredictions({ input: inputValue }, (predictions) => {
            setOptions(predictions || []);
        });
    }, [inputValue, isApiLoaded]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
        setShowOptions(true);
    };

    const handleOptionClick = (
        option: google.maps.places.AutocompletePrediction,
    ) => {
        // Fetch full place details
        if (!isApiLoaded) return;
        const placesService = new google.maps.places.PlacesService(
            document.createElement("div"),
        );
        placesService.getDetails(
            { placeId: option.place_id },
            (place, status) => {
                if (
                    status === google.maps.places.PlacesServiceStatus.OK &&
                    place
                ) {
                    setValue(place);
                    setInputValue(option.description);
                    setShowOptions(false);
                    setOptions([]);
                }
            },
        );
    };

    // Hide options on blur
    const handleBlur = () => {
        setTimeout(() => setShowOptions(false), 100); // Delay to allow click
    };

    return (
        <div className="relative w-full">
            <If condition={!!children}>{children}</If>
            <If condition={!children}>
                {label && <Label>Location</Label>}
                <Input
                    prefixComponent={
                        <RiMapPinLine className="mr-1 text-v3-N-300" />
                    }
                    ref={inputRef}
                    type="text"
                    className={cn(className, "cursor-pointer")}
                    id="locationPlacesInput"
                    value={inputValue}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onFocus={() => setShowOptions(true)}
                    placeholder={placeholder}
                    required={required}
                    autoComplete="off"
                />
                {showOptions && options.length > 0 && (
                    <ul className="mt-1 max-h-60 overflow-auto rounded-md border shadow-lg">
                        {options.map((option) => (
                            <li
                                key={option.place_id}
                                className="flex cursor-pointer items-center gap-2 px-4 py-2 "
                                onMouseDown={() => handleOptionClick(option)}
                            >
                                <RiMapPinLine className="text-v3-N-300" />
                                <span>{option.description}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </If>
        </div>
    );
};

export default GoogleMapsAutoComplete;

export type GooglePlace = ReturnType<
    google.maps.places.Autocomplete["getPlace"]
>;

type Coordinates = {
    longitude: number | null;
    latitude: number | null;
};

export const extractCoordinates = (
    geometry: google.maps.places.PlaceGeometry | undefined,
): Coordinates => {
    if (!geometry?.location) {
        return {
            longitude: null,
            latitude: null,
        };
    }

    const lat = geometry.location.lat();
    const lng = geometry.location.lng();

    return {
        longitude: isNaN(lng) ? null : lng,
        latitude: isNaN(lat) ? null : lat,
    };
};

export const extractCityFromGooglePlace = (place: GooglePlace | undefined) => {
    if (!place || !place.address_components) return null;
    const cityComponent = place.address_components.find(
        (component) =>
            component.types.includes("locality") ||
            component.types.includes("administrative_area_level_1"),
    );

    return cityComponent ? cityComponent.long_name : null;
};

export const useGooglePlace = (
    place_id?: string | null,
): [GooglePlace | undefined, (value: GooglePlace | undefined) => void] => {
    const [value, setValue] = useState<GooglePlace | undefined>();

    // query the google api for the place so we can set a initial state IF it has one
    useEffect(() => {
        if (!place_id) {
            setValue(undefined);
            return;
        }

        const service = new google.maps.places.PlacesService(
            document.createElement("div"),
        );

        service.getDetails(
            {
                placeId: place_id,
            },
            (place, status) => {
                if (
                    status === google.maps.places.PlacesServiceStatus.OK &&
                    place
                ) {
                    setValue(place);
                }
            },
        );
    }, [place_id]);

    const setGooglePlace = (value: GooglePlace | undefined) => {
        setValue(value);
    };

    return [value, setGooglePlace];
};
